import { formatDate } from '@/lib/utils/format';
import type { LocationLaunch } from '@/types/location-launches';
import type { LocationOrder, LocationOrderItem } from '@/types/location-orders';

type LaunchLike = Pick<
  LocationLaunch,
  'status' | 'installation_date' | 'excluded_from_upcoming_launches' | 'is_primary' | 'launch_date'
>;

export type EquipmentLaunchSourceState =
  | 'unlinked'
  | 'missing_launch'
  | 'active_primary'
  | 'active_additional'
  | 'hidden_primary'
  | 'hidden_additional'
  | 'cancelled_primary'
  | 'cancelled_additional';

export interface AssignableEquipmentLaunchOption {
  id: string;
  launch: LocationLaunch;
  isPrimary: boolean;
  label: string;
  description: string;
}

export interface ImportableEquipmentOrder {
  order: LocationOrder;
  launch: LocationLaunch | null;
  sourceState: EquipmentLaunchSourceState;
  sourceLabel: string;
}

export interface AssignedEquipmentGroup {
  launchId: string | null;
  launch: LocationLaunch | null;
  launchLabel: string;
  launchDescription: string;
  isPrimary: boolean;
  displayTypeId: string;
  quantity: number;
  orderIds: string[];
  orderItemIds: string[];
  sourceState: EquipmentLaunchSourceState;
  sourceLabel: string;
}

function startOfDay(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

export function calculateDaysUntilLaunchDate(
  installationDate: string | null,
  referenceDate = new Date()
): number | null {
  if (!installationDate) return null;

  const today = startOfDay(referenceDate);
  const targetDate = startOfDay(new Date(installationDate));
  return Math.ceil((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export function isLaunchInUpcomingWindow(
  launch: LaunchLike,
  options?: { pastDays?: number; futureDays?: number; referenceDate?: Date }
): boolean {
  const pastDays = options?.pastDays ?? 60;
  const futureDays = options?.futureDays ?? 60;
  const daysUntil = calculateDaysUntilLaunchDate(launch.installation_date, options?.referenceDate);

  return (
    launch.status !== 'cancelled' &&
    launch.status !== 'launched_active' &&
    !launch.excluded_from_upcoming_launches &&
    daysUntil !== null &&
    daysUntil >= -pastDays &&
    daysUntil <= futureDays
  );
}

export function isRelevantLaunch(
  launch: LaunchLike,
  options?: { recentDays?: number; referenceDate?: Date }
): boolean {
  if (launch.status === 'cancelled') return false;
  if (launch.status !== 'launched_active') return true;

  const recentDays = options?.recentDays ?? 60;
  const daysUntil = calculateDaysUntilLaunchDate(launch.installation_date, options?.referenceDate);
  return daysUntil !== null && daysUntil >= -recentDays;
}

export function sortLaunchesByInstallationDate(a: LaunchLike, b: LaunchLike): number {
  if (!a.installation_date && !b.installation_date) return 0;
  if (!a.installation_date) return 1;
  if (!b.installation_date) return -1;
  return new Date(a.installation_date).getTime() - new Date(b.installation_date).getTime();
}

export function getEquipmentLaunchSourceState(
  launch: Pick<LocationLaunch, 'status' | 'excluded_from_upcoming_launches' | 'is_primary'> | null
): EquipmentLaunchSourceState {
  if (!launch) {
    return 'unlinked';
  }

  if (launch.status === 'cancelled') {
    return launch.is_primary ? 'cancelled_primary' : 'cancelled_additional';
  }

  if (launch.excluded_from_upcoming_launches) {
    return launch.is_primary ? 'hidden_primary' : 'hidden_additional';
  }

  return launch.is_primary ? 'active_primary' : 'active_additional';
}

export function getEquipmentLaunchLabel(
  launch: Pick<LocationLaunch, 'is_primary' | 'installation_date'> | null
): string {
  if (!launch) {
    return 'Unassigned Equipment';
  }

  if (launch.is_primary) {
    return 'Primary Launch';
  }

  if (launch.installation_date) {
    return `${formatDate(launch.installation_date)} Launch`;
  }

  return 'Additional Launch';
}

export function getEquipmentLaunchDescription(
  launch: Pick<
    LocationLaunch,
    'status' | 'excluded_from_upcoming_launches' | 'is_primary' | 'installation_date' | 'launch_date'
  > | null
): string {
  if (!launch) {
    return 'Not linked to any launch yet';
  }

  const dateLabel = launch.installation_date
    ? `Install ${formatDate(launch.installation_date)}`
    : launch.launch_date
      ? `Launch ${formatDate(launch.launch_date)}`
      : 'No scheduled dates';

  if (launch.status === 'cancelled') {
    return `${dateLabel} • Cancelled`;
  }

  if (launch.excluded_from_upcoming_launches) {
    return `${dateLabel} • Hidden from Upcoming Launches`;
  }

  return dateLabel;
}

export function getEquipmentSourceLabel(
  launch: Pick<
    LocationLaunch,
    'status' | 'excluded_from_upcoming_launches' | 'is_primary' | 'installation_date'
  > | null
): string {
  const sourceState = getEquipmentLaunchSourceState(launch);

  switch (sourceState) {
    case 'unlinked':
      return 'Currently unlinked';
    case 'missing_launch':
      return 'Currently linked to a missing launch';
    case 'hidden_primary':
      return 'Currently tied to hidden primary launch';
    case 'hidden_additional':
      return launch?.installation_date
        ? `Currently tied to hidden ${formatDate(launch.installation_date)} launch`
        : 'Currently tied to hidden additional launch';
    case 'cancelled_primary':
      return 'Currently tied to cancelled primary launch';
    case 'cancelled_additional':
      return launch?.installation_date
        ? `Currently tied to cancelled ${formatDate(launch.installation_date)} launch`
        : 'Currently tied to cancelled additional launch';
    case 'active_primary':
      return 'Currently tied to primary launch';
    case 'active_additional':
      return launch?.installation_date
        ? `Currently tied to ${formatDate(launch.installation_date)} launch`
        : 'Currently tied to active additional launch';
    default:
      return 'Launch assignment available';
  }
}

export function getAssignableEquipmentLaunches(
  launches: LocationLaunch[]
): AssignableEquipmentLaunchOption[] {
  const primaryLaunch = launches.find((launch) => launch.is_primary) ?? null;
  const additionalLaunches = launches
    .filter(
      (launch) =>
        !launch.is_primary &&
        !launch.excluded_from_upcoming_launches &&
        launch.status !== 'cancelled' &&
        isRelevantLaunch(launch)
    )
    .sort(sortLaunchesByInstallationDate);

  const orderedLaunches = [
    ...(primaryLaunch ? [primaryLaunch] : []),
    ...additionalLaunches,
  ];

  return orderedLaunches.map((launch) => ({
    id: launch.id,
    launch,
    isPrimary: launch.is_primary,
    label: getEquipmentLaunchLabel(launch),
    description: getEquipmentLaunchDescription(launch),
  }));
}

export function getImportableEquipmentOrders(
  orders: LocationOrder[],
  launches: LocationLaunch[]
): ImportableEquipmentOrder[] {
  const launchesById = new Map(launches.map((launch) => [launch.id, launch]));
  const importableOrders: ImportableEquipmentOrder[] = [];

  for (const order of orders) {
    if (!order.launch_id) {
      importableOrders.push({
        order,
        launch: null,
        sourceState: 'unlinked',
        sourceLabel: getEquipmentSourceLabel(null),
      });
      continue;
    }

    const launch = launchesById.get(order.launch_id) ?? null;
    if (!launch) {
      importableOrders.push({
        order,
        launch: null,
        sourceState: 'missing_launch',
        sourceLabel: 'Currently linked to a missing launch',
      });
      continue;
    }

    const sourceState = getEquipmentLaunchSourceState(launch);
    if (sourceState === 'active_primary' || sourceState === 'active_additional') {
      continue;
    }

    importableOrders.push({
      order,
      launch,
      sourceState,
      sourceLabel: getEquipmentSourceLabel(launch),
    });
  }

  return importableOrders;
}

export function buildAssignedEquipmentGroups(input: {
  orderItems: LocationOrderItem[];
  orders: LocationOrder[];
  launches: LocationLaunch[];
}): AssignedEquipmentGroup[] {
  const ordersById = new Map(input.orders.map((order) => [order.id, order]));
  const launchesById = new Map(input.launches.map((launch) => [launch.id, launch]));
  const grouped = new Map<string, AssignedEquipmentGroup>();

  for (const item of input.orderItems) {
    const order = ordersById.get(item.order_id);
    const launchId = order?.launch_id ?? null;
    const launch = launchId ? launchesById.get(launchId) ?? null : null;
    const groupKey = `${launchId ?? 'unlinked'}:${item.display_type_id}`;
    const existingGroup = grouped.get(groupKey);

    if (existingGroup) {
      existingGroup.quantity += item.quantity;
      existingGroup.orderItemIds.push(item.id);
      if (order && !existingGroup.orderIds.includes(order.id)) {
        existingGroup.orderIds.push(order.id);
      }
      continue;
    }

    const sourceState = launchId && !launch ? 'missing_launch' : getEquipmentLaunchSourceState(launch);
    grouped.set(groupKey, {
      launchId,
      launch,
      launchLabel: launchId && !launch ? 'Missing Launch' : getEquipmentLaunchLabel(launch),
      launchDescription:
        launchId && !launch ? 'Linked launch record could not be found' : getEquipmentLaunchDescription(launch),
      isPrimary: launch?.is_primary ?? false,
      displayTypeId: item.display_type_id,
      quantity: item.quantity,
      orderIds: order ? [order.id] : [],
      orderItemIds: [item.id],
      sourceState,
      sourceLabel:
        launchId && !launch ? 'Currently linked to a missing launch' : getEquipmentSourceLabel(launch),
    });
  }

  return [...grouped.values()].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) {
      return a.isPrimary ? -1 : 1;
    }

    if (a.launch && b.launch) {
      const launchSort = sortLaunchesByInstallationDate(a.launch, b.launch);
      if (launchSort !== 0) {
        return launchSort;
      }
    } else if (a.launch && !b.launch) {
      return -1;
    } else if (!a.launch && b.launch) {
      return 1;
    }

    return a.displayTypeId.localeCompare(b.displayTypeId);
  });
}

export function launchMatchesOriginalDates(
  launch: Pick<LocationLaunch, 'installation_date' | 'launch_date'>,
  originalInstallationDate: string | null,
  originalLaunchDate: string | null
): boolean {
  if (!originalInstallationDate && !originalLaunchDate) {
    return false;
  }

  if (originalInstallationDate && launch.installation_date !== originalInstallationDate) {
    return false;
  }

  if (originalLaunchDate && launch.launch_date !== originalLaunchDate) {
    return false;
  }

  return true;
}
